import { timerHelperModule } from '../../helpers/timer-helper';
import { appConfig } from '../../constants/app-config';
import { firebaseModule } from '../../vendor/firebase-module';

export const firebaseServiceModule = angular
  .module('Q.services.firebase', [firebaseModule, timerHelperModule])

  /**
   * Provides a service that makes interacting with Firebase in an asynchronous manner easier.
   * This service wraps up a lot of the angularfire $firebaseObject methods in simple utility methods
   * that enable automatic configuring of FB only once it's needed, and low-effort access to some useful
   * features, like using onDisconnect to signal logged in status.
   */
  .factory('firebase', function ($firebaseObject, $q, timerHelper) {
    let fb = null;
    const REMOVE_ON_DISCONNECT = {};

    // 10 seconds to assume a failed connection
    const CONNECTION_FAILURE_TIMEOUT = 10000;

    return {
      init,
      ifConnected,
      get,
      bind,
      set,
      setWhenConnected,
      remove,
      REMOVE_ON_DISCONNECT,
    };

    /**
     * Initializes Firebase
     * Note: this is purposefully async in case we decide to only load the FB code when it's being used.
     * @returns {Promise}
     */
    function init() {
      if (fb === null) {
        fb = window.firebase;
        if (!fb) {
          return $q.reject(new Error('Unable to load Firebase'));
        }

        // if on staging, we have to configure a different firebase manually.
        let config = appConfig.firebase;
        const Modernizr = window && window.Modernizr;
        if (
          Modernizr &&
          Modernizr['qualified-staging'] &&
          appConfig.firebaseStaging
        ) {
          config = appConfig.firebaseStaging;
        }
        fb.initializeApp(config);
      }
      return $q.when(fb);
    }

    /**
     * Returns a promise that resolves if the connection is up or resolved within the specified timeout
     * @param timeout {Number?} Seconds to timeout, defaults to 10s
     * @returns {Promise} A promise that resolves if the connection is up within the timeout
     */
    function ifConnected(timeout) {
      return init().then((fb) => {
        return $q((resolve, reject) => {
          let onlineCheck = fb.database().ref('.info/connected');

          let notConnectedTimer = null;

          function initCheck(connectedCheck) {
            // determine if we've connected or not
            if (connectedCheck.val() === true) {
              // if we've connected, we're set
              onlineCheck.off('value', initCheck);
              timerHelper.longTimeout.cancel(notConnectedTimer);
              resolve(fb);
            } else if (!notConnectedTimer) {
              // if we don't connect within the timeout, reject
              notConnectedTimer = timerHelper.longTimeout(() => {
                // unable to connect in a timely fashion
                onlineCheck.off('value', initCheck);
                reject(new Error('Unable to connect to Firebase'));
              }, timeout || CONNECTION_FAILURE_TIMEOUT);
            }
          }

          onlineCheck.on('value', initCheck);
        });
      });
    }

    /**
     * Utility method to make it easier to retrieve an AngularFire reference to a Firebase node
     * @param ref ID String for the Firebase key
     * @returns {Promise}
     */
    function get(ref) {
      return init().then((fb) =>
        $firebaseObject(fb.database().ref(ref)).$loaded(),
      );
    }

    /**
     * Utility method to quickly bind a reference to a scope variable
     * @param ref
     * @param scope
     * @param varName
     * @returns {Promise}
     */
    function bind(ref, scope, varName) {
      return get(ref).then((obj) =>
        obj.$bindTo(scope, varName).then(() => obj),
      );
    }

    /**
     * Utility method to easily set a value you don't need to track
     * @param ref ID String for the Firebase key
     * @param value Value to store
     * @param onDisconnect If provided, set this ref to the provided value when the user gets disconnected
     * @returns {Promise}
     */
    function set(ref, value, { onDisconnect } = {}) {
      return get(ref).then((obj) => {
        if (onDisconnect !== undefined) {
          if (onDisconnect === REMOVE_ON_DISCONNECT) {
            obj.$ref().onDisconnect().remove();
          } else {
            obj.$ref().onDisconnect().set(onDisconnect);
          }
        }
        return _setFirebaseObject(obj, value);
      });
    }

    /**
     * Utility method to set a value when connected, and clear it or update it when you aren't
     * @param ref
     * @param value
     * @param onDisconnect
     * @returns {Function} manual disconnect function, which also removes the change event listener
     */
    function setWhenConnected(ref, value, onDisconnect = REMOVE_ON_DISCONNECT) {
      let _disconnect = null;

      function disconnect() {
        if (_disconnect) {
          _disconnect();
          _disconnect = null;
        }
      }

      disconnect.promise = get(ref).then((obj) => {
        let onlineCheck = fb.database().ref('.info/connected');

        function onValueChange(snapshot) {
          if (snapshot.val()) {
            if (onDisconnect === REMOVE_ON_DISCONNECT) {
              obj.$ref().onDisconnect().remove();
            } else {
              obj.$ref().onDisconnect().set(onDisconnect);
            }
            return _setFirebaseObject(obj, value);
          }
          return obj.$save().then(() => obj);
        }

        onlineCheck.on('value', onValueChange);

        _disconnect = function () {
          onlineCheck.off('value', onValueChange);
          if (onDisconnect === REMOVE_ON_DISCONNECT) {
            obj.$ref().remove();
          } else {
            obj.$ref().set(onDisconnect);
          }
        };

        return obj;
      });

      disconnect.update = function () {
        return disconnect.promise.then((obj) => _setFirebaseObject(obj, value));
      };

      return disconnect;
    }

    /**
     * Utility method to quickly remove a Firebase value
     * @param ref ID String for the Firebase key
     * @returns {Promise}
     */
    function remove(ref) {
      return init().then((fb) => {
        let obj = $firebaseObject(fb.database().ref(ref));
        return obj.$remove();
      });
    }

    /**
     * Sets the $firebaseObject obj values. If value is a primitive, this automatically handles setting
     * $value.  If it is an object, it extends the values in.
     *
     * @param obj Object to update
     * @param value Value or values to set
     * @returns {Promise}
     * @private
     */
    function _setFirebaseObject(obj, value) {
      if (angular.isObject(value)) {
        for (let k in obj) {
          if (obj.hasOwnProperty(k) && k[0] !== '$') {
            delete obj[k];
          }
        }
        angular.extend(obj, value);
        stripUndefined(obj);
      } else {
        obj.$value = value;
      }
      return obj.$save().then(() => obj);
    }

    function stripUndefined(obj) {
      angular.forEach(obj, (v, k) => {
        if (v === undefined) {
          delete obj[k];
        } else if (Array.isArray(v)) {
          v.forEach(stripUndefined);
        } else if (angular.isObject(v)) {
          stripUndefined(v);
        }
      });
    }
  });
